<template>
  <div class="md-chart">
    <div v-if="chartTitle" class="chart-title">{{ chartTitle }}</div>
    <div v-if="loading" class="chart-loading">
      <i class="el-icon-loading"></i> {{ loadingText }}
    </div>
    <div v-else-if="loadError" class="chart-error">{{ loadError }}</div>
    <MdRenderFallback
      v-else-if="renderFailed"
      reason="render_error"
      component-name="chart"
      :raw="sourceJson || _rawJson"
      @fixed="handleFallbackFixed"
    />
    <div v-else ref="chartContainer" class="chart-container"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'
import MdRenderFallback from './MdRenderFallback.vue'
import { reparseComponentJson } from '../md-renderer'

// ============================================================================
// 图表设计令牌 (参考 COSMO-Sphere-Client MarkdownChartBlock.tsx)
// 8 色调色板(品牌色打头) + 文字色三级分级 + 表面色 + tooltip/轴线柔和色
// ============================================================================
const LIGHT_TOKENS = {
  palette: [
    '#8453ed', // accent  紫色(品牌主色)
    '#2f7ff5', // 蓝
    '#0d9aa8', // 青
    '#098926', // 绿
    '#ed990e', // 橙
    '#e5484d', // 红
    '#d94bb0', // 粉
    '#8a94a6' // 灰
  ],
  textPrimary: '#111317',
  textSecondary: '#797b7f',
  textMuted: '#aaabaf',
  surface: '#f7f8fa',
  tooltipBackground: '#ffffff',
  tooltipBorder: 'rgba(17, 19, 23, 0.08)',
  tooltipShadow: '0 8px 24px rgba(17, 19, 23, 0.12), 0 2px 6px rgba(17, 19, 23, 0.08)',
  axisLine: 'rgba(17, 19, 23, 0.10)',
  gridLine: 'rgba(17, 19, 23, 0.06)',
  crosshair: 'rgba(17, 19, 23, 0.22)'
}

// hex (#rgb / #rrggbb) -> rgba() 字符串,用于渐变填充色 stop
function withAlpha(hex, alpha) {
  let value = String(hex).replace('#', '')
  if (value.length === 3) {
    value = value.split('').map(c => c + c).join('')
  }
  const int = parseInt(value, 16)
  const r = (int >> 16) & 255
  const g = (int >> 8) & 255
  const b = int & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// 读取 series 实际使用的颜色(用于派生样式如渐变填充)
function readSeriesColor(series, tokens) {
  const { lineStyle, itemStyle } = series
  if (lineStyle && typeof lineStyle.color === 'string') return lineStyle.color
  if (itemStyle && typeof itemStyle.color === 'string') return itemStyle.color
  return tokens.palette[0]
}

// 深合并:override 覆盖 base,数组和对象递归合并
function mergeChartOption(base, override) {
  const out = { ...base }
  for (const [key, value] of Object.entries(override)) {
    if (value === undefined || value === null) continue
    const current = out[key]
    if (isPlainObject(value) && isPlainObject(current)) {
      out[key] = mergeChartOption(current, value)
      continue
    }
    if (Array.isArray(value) && Array.isArray(current)) {
      out[key] = value.map((item, i) =>
        isPlainObject(item) && isPlainObject(current[i]) ? mergeChartOption(current[i], item) : item
      )
      continue
    }
    out[key] = value
  }
  return out
}

function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

// 按系列类型应用视觉增强(bar/line/pie),用户传入值始终覆盖默认
function styleSeries(series, tokens, isMulti) {
  if (!isPlainObject(series)) return series
  const type = typeof series.type === 'string' ? series.type.toLowerCase() : ''

  if (type === 'bar') {
    return mergeChartOption(
      {
        barMaxWidth: 32,
        itemStyle: { borderRadius: 4 },
        emphasis: { focus: 'series' }
      },
      series
    )
  }

  if (type === 'line') {
    const lineColor = readSeriesColor(series, tokens)
    const defaults = {
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2.5, cap: 'round' },
      emphasis: { focus: 'series' }
    }
    // 单系列折线图加柔和渐变填充;多系列不加(避免区域重叠浑浊)
    if (!isMulti && series.areaStyle === undefined) {
      defaults.areaStyle = {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: withAlpha(lineColor, 0.18) },
            { offset: 1, color: withAlpha(lineColor, 0) }
          ]
        }
      }
    }
    return mergeChartOption(defaults, series)
  }

  if (type === 'pie') {
    return mergeChartOption(
      {
        itemStyle: { borderColor: tokens.surface, borderWidth: 2, borderRadius: 6 },
        label: { color: tokens.textSecondary, fontSize: 12 },
        labelLayout: { hideOverlap: true },
        emphasis: { scale: true, scaleSize: 6 }
      },
      series
    )
  }

  return series
}

// 轴样式美化:隐藏刻度线 + Y轴虚线网格 + 轴线柔和色
function styleAxis(axis, isY, tokens) {
  if (!isPlainObject(axis)) return axis
  return mergeChartOption(
    {
      axisLine: { show: !isY, lineStyle: { color: tokens.axisLine }},
      axisTick: { show: false },
      axisLabel: {
        color: tokens.textSecondary,
        fontSize: 12,
        hideOverlap: true,
        margin: 10
      },
      splitLine: {
        show: isY,
        lineStyle: { color: tokens.gridLine, type: 'dashed' }
      },
      nameTextStyle: { color: tokens.textMuted, fontSize: 11 }
    },
    axis
  )
}

// 默认图表配置常量 - 融入设计令牌
const DEFAULT_OPTION = {
  backgroundColor: 'transparent',
  color: LIGHT_TOKENS.palette,
  textStyle: {
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    fontSize: 12,
    color: LIGHT_TOKENS.textSecondary
  },
  animationDuration: 500,
  animationEasing: 'cubicOut',
  grid: {
    left: '5%',
    right: '5%',
    top: 110,
    bottom: 45,
    containLabel: true
  },
  tooltip: {
    backgroundColor: LIGHT_TOKENS.tooltipBackground,
    borderColor: LIGHT_TOKENS.tooltipBorder,
    borderWidth: 1,
    padding: [8, 12],
    textStyle: {
      color: LIGHT_TOKENS.textPrimary,
      fontSize: 12,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
    },
    extraCssText: `border-radius: 8px; box-shadow: ${LIGHT_TOKENS.tooltipShadow};`,
    axisPointer: {
      type: 'line',
      lineStyle: { color: LIGHT_TOKENS.crosshair, width: 1, type: 'dashed' },
      crossStyle: { color: LIGHT_TOKENS.crosshair, width: 1, type: 'dashed' }
    }
  }
}

// 响应式配置 - 根据容器宽度自适应
const getResponsiveConfig = (width) => {
  if (width < 350) {
    // 超窄屏
    return {
      grid: { left: '4%', right: '4%', top: 98, bottom: 38 },
      axisLabel: { fontSize: 10 }
    }
  } else if (width < 500) {
    // 窄屏
    return {
      grid: { left: '5%', right: '5%', top: 108, bottom: 42 },
      axisLabel: { fontSize: 10 }
    }
  } else if (width < 700) {
    // 中屏
    return {
      grid: { left: '5%', right: '5%', top: 114, bottom: 48 },
      axisLabel: { fontSize: 10 }
    }
  }
  // 宽屏
  return {
    grid: { left: '5%', right: '5%', top: 118, bottom: 52 },
    axisLabel: { fontSize: 12 }
  }
}

// 图表类型判断
const getChartType = (option) => {
  if (!option || !option.series || !Array.isArray(option.series)) return 'line'
  const firstSeries = option.series[0] || {}
  return firstSeries.type || 'line'
}

// 计算Y轴标签所需空间
const calculateYAxisSpace = (option) => {
  if (!option.yAxis) return 0

  const yAxes = Array.isArray(option.yAxis) ? option.yAxis : [option.yAxis]
  let maxLabelLength = 0

  yAxes.forEach(axis => {
    // 从 axisLabel.formatter 或 data 估算标签长度
    const labels = axis.data || []
    const name = axis.name || ''

    // 检查 formatter 是否存在，使用默认最大长度
    if (axis.axisLabel && axis.axisLabel.formatter) {
      maxLabelLength = Math.max(maxLabelLength, 5) // 默认5个字符估算
    } else if (labels.length > 0) {
      const maxLen = labels.reduce((max, label) => {
        return String(label).length > max ? String(label).length : max
      }, 0)
      maxLabelLength = Math.max(maxLabelLength, maxLen)
    }

    // 加上 axis.name 的长度
    if (name) {
      maxLabelLength = Math.max(maxLabelLength, name.length)
    }
  })

  // 估算：每个字符 4px，加上 padding
  // 至少 0px (无标签时)，最多 60px
  return Math.min(Math.max(maxLabelLength * 4 + 8, 0), 60)
}

// 智能图例位置
const getSmartLegendConfig = (option, containerWidth) => {
  const yAxisSpace = calculateYAxisSpace(option)
  const totalLegendItems = (option.legend && option.legend.data ? option.legend.data.length : 0)

  // 规则1：Y轴标签很长 (>50px)，图例必须放底部
  if (yAxisSpace > 50 && totalLegendItems > 2) {
    return {
      show: true,
      type: 'scroll',
      bottom: 0,
      top: 'auto',
      left: 'center',
      right: 'auto',
      orient: 'horizontal',
      itemWidth: 12,
      itemHeight: 8,
      itemGap: 10,
      textStyle: { fontSize: 11, color: '#666' }
    }
  }

  // 规则2：容器较窄 (< 500px)，图例放底部
  if (containerWidth < 500 && totalLegendItems > 1) {
    return {
      show: true,
      type: 'scroll',
      bottom: 0,
      top: 'auto',
      left: 'center',
      right: 'auto',
      itemWidth: 10,
      itemHeight: 6,
      itemGap: 8,
      textStyle: { fontSize: 10, color: '#666' }
    }
  }

  // 规则3：图例项多 (>4)，放底部
  if (totalLegendItems > 4) {
    return {
      show: true,
      type: 'scroll',
      bottom: 0,
      top: 'auto',
      left: 'center',
      right: 'auto',
      itemWidth: 12,
      itemHeight: 8,
      itemGap: 10,
      textStyle: { fontSize: 11, color: '#666' }
    }
  }

  // 规则4：正常情况，图例放标题下方（留出标题空间）
  return {
    show: true,
    type: 'scroll',
    top: 26, // 紧贴标题下方
    right: 15,
    itemWidth: 12,
    itemHeight: 8,
    itemGap: 10,
    textStyle: { fontSize: 11, color: '#666' }
  }
}

// 智能网格配置
const getSmartGridConfig = (option, containerWidth, legendBottom) => {
  const responsive = getResponsiveConfig(containerWidth)
  const hasTitle = !!(option.title && option.title.text)

  const grid = {
    left: responsive.grid.left,
    right: responsive.grid.right,
    top: hasTitle ? responsive.grid.top : responsive.grid.top - 15,
    bottom: legendBottom ? 60 : responsive.grid.bottom,
    containLabel: true
  }

  // 如果图例在底部，增加 bottom 空间
  if (legendBottom) {
    grid.bottom = Math.max(grid.bottom, 65)
  }

  // containLabel: true 会自动处理 Y 轴标签空间，无需手动调整 left

  return grid
}

// 合并默认配置
const mergeWithDefault = (userOption, containerWidth) => {
  if (!userOption || typeof userOption !== 'object') return userOption

  const result = JSON.parse(JSON.stringify(userOption))
  const chartType = getChartType(result)
  const responsive = getResponsiveConfig(containerWidth || 600)

  // 特殊图表类型处理
  if (chartType === 'pie') {
    // 饼图：图例放底部
    const pieLegend = {
      show: true,
      type: 'scroll',
      bottom: 0,
      top: 'auto',
      left: 'center',
      right: 'auto',
      itemWidth: 12,
      itemHeight: 8,
      textStyle: { fontSize: 11, color: LIGHT_TOKENS.textSecondary }
    }
    result.legend = { ...pieLegend, ...(result.legend || {}) }

    result.grid = {
      left: '5%',
      right: '5%',
      top: '10%',
      bottom: '15%',
      containLabel: true
    }
    // 应用设计令牌：调色板 + tooltip 美化 + 系列视觉增强（圆角/边框/高亮缩放）
    if (!result.color) result.color = LIGHT_TOKENS.palette
    if (!result.tooltip) result.tooltip = {}
    result.tooltip = {
      ...DEFAULT_OPTION.tooltip,
      ...result.tooltip,
      textStyle: {
        ...DEFAULT_OPTION.tooltip.textStyle,
        ...((result.tooltip.textStyle) || {})
      }
    }
    if (Array.isArray(result.series)) {
      result.series = result.series.map(s => styleSeries(s, LIGHT_TOKENS, result.series.length > 1))
    }
    return result
  }

  if (chartType === 'scatter') {
    // 散点图：不显示图例
    result.legend = { show: false }
    result.grid = {
      ...DEFAULT_OPTION.grid,
      ...responsive.grid,
      ...(result.grid || {}),
      containLabel: true
    }
    // 应用设计令牌：调色板 + tooltip 美化
    if (!result.color) result.color = LIGHT_TOKENS.palette
    if (!result.tooltip) result.tooltip = {}
    result.tooltip = {
      ...DEFAULT_OPTION.tooltip,
      ...result.tooltip,
      textStyle: {
        ...DEFAULT_OPTION.tooltip.textStyle,
        ...((result.tooltip.textStyle) || {})
      }
    }
    return result
  }

  // 计算智能布局
  const legendConfig = getSmartLegendConfig(result, containerWidth)
  const legendBottom = legendConfig.bottom !== undefined && legendConfig.bottom !== 'auto'
  const gridConfig = getSmartGridConfig(result, containerWidth, legendBottom)

  // 应用图例配置
  if (!result.legend) {
    result.legend = legendConfig
  } else {
    result.legend = {
      ...legendConfig,
      ...result.legend,
      textStyle: {
        ...legendConfig.textStyle,
        ...(result.legend.textStyle || {})
      }
    }
  }

  // 应用网格配置
  if (result.grid) {
    result.grid = {
      ...gridConfig,
      ...result.grid,
      containLabel: true
    }
  } else {
    result.grid = gridConfig
  }

  // X 轴类目轴 + 旋转标签：增加 grid.bottom 容纳旋转后的刻度名，避免向下溢出
  if (result.xAxis) {
    const xAxesCheck = Array.isArray(result.xAxis) ? result.xAxis : [result.xAxis]
    const hasXCategory = xAxesCheck.some(a => a && Array.isArray(a.data) && (!a.type || a.type === 'category'))
    if (hasXCategory) {
      // 旋转 30° 后预留 65px 容纳标签，避免向下溢出
      result.grid.bottom = Math.max(result.grid.bottom || 45, result.legend?.bottom !== undefined && result.legend?.bottom !== 'auto' ? 85 : 65)
    }
  }

  // Y 轴：用 styleAxis 美化（隐藏刻度线 + Y轴虚线网格 + 轴线柔和色）
  if (result.yAxis) {
    const yAxes = Array.isArray(result.yAxis) ? result.yAxis : [result.yAxis]
    result.yAxis = yAxes.map(axis => styleAxis({
      ...axis,
      axisLabel: {
        fontSize: responsive.axisLabel.fontSize,
        ...((axis.axisLabel) || {})
      }
    }, true, LIGHT_TOKENS))
  }

  // X 轴：用 styleAxis 美化 + 默认 interval:0/rotate:30（长刻度完整展示）
  if (result.xAxis) {
    const xAxes = Array.isArray(result.xAxis) ? result.xAxis : [result.xAxis]
    result.xAxis = xAxes.map(axis => styleAxis({
      ...axis,
      axisLabel: {
        // 默认显示所有刻度并倾斜 30°，避免类目标签过长时被省略或重叠
        interval: 0,
        rotate: 30,
        fontSize: responsive.axisLabel.fontSize,
        // 用户在 option.xAxis.axisLabel 中传入的 interval/rotate 会覆盖这两个默认值
        ...((axis.axisLabel) || {})
      }
    }, false, LIGHT_TOKENS))
  }

  // 标题：保留用户样式，调整位置增加顶部间距
  if (result.title) {
    result.title = {
      top: 10,
      left: 'center',
      ...result.title,
      textStyle: {
        fontSize: 13,
        fontWeight: 'bold',
        color: '#1f2329',
        ...(result.title.textStyle || {})
      }
    }
  }

  // Tooltip：统一样式
  if (!result.tooltip) {
    result.tooltip = { ...DEFAULT_OPTION.tooltip }
  } else {
    result.tooltip = {
      ...DEFAULT_OPTION.tooltip,
      ...result.tooltip,
      textStyle: {
        ...DEFAULT_OPTION.tooltip.textStyle,
        ...(result.tooltip.textStyle || {})
      }
    }
  }

  // 柱状图：容器较小时隐藏柱子上的数字标签
  if (chartType === 'bar' && containerWidth < 400 && result.series) {
    result.series.forEach(s => {
      if (s.label && s.label.show !== false) {
        s.label = { ...s.label, show: false }
      }
    })
  }

  // 系列视觉增强：bar 圆角+barMaxWidth / line 平滑+单系列渐变填充 / pie 圆角+高亮缩放
  // 用户在 option.series[].itemStyle/lineStyle/areaStyle 等传入的值始终覆盖默认
  // 应用设计令牌：调色板（用户未指定 color 时用品牌8色调色板）
  if (!result.color) result.color = LIGHT_TOKENS.palette
  if (Array.isArray(result.series)) {
    const isMulti = result.series.length > 1
    result.series = result.series.map(s => styleSeries(s, LIGHT_TOKENS, isMulti))
  }

  return result
}

export default {
  name: 'MdChart',
  components: { MdRenderFallback },
  props: {
    type: { type: String, default: 'line' },
    title: { type: String, default: '' },
    data: { type: String, default: '' },
    xfield: { type: String, default: '' },
    yfield: { type: String, default: '' },
    option: { type: String, default: '' },
    _rawJson: { type: String, default: '' },
    // 完整原始卡片文本，仅用于渲染失败兜底展示，不参与图表渲染
    sourceJson: { type: String, default: '' },
    _componentKey: { type: String, default: '' }
  },
  data() {
    return {
      chart: null,
      loading: false,
      loadError: '',
      // 图表配置本身无法渲染（区别于接口请求失败）
      renderFailed: false,
      resolvedOption: null,
      loadingText: '',
      lastContainerWidth: 0,
      lastContainerHeight: 0,
      sourceOption: null,
      // 校验修复器返回的修复后 JSON，优先于 _rawJson 参与渲染
      fixedRawJson: ''
    }
  },
  computed: {
    chartTitle() {
      return this.title
    },
    parsedConfig() {
      return this._safeParseJson(this.fixedRawJson || this._rawJson) || {}
    },
    parsedOption() {
      // 从 _rawJson 中提取 option 值
      let opt = this.parsedConfig.option || null
      // 兜底：从 props.option 直接解析
      if (!opt) {
        opt = this._safeParseJson(this.option) || null
      }

      // 关键修复：_rawJson 经过 JSON.stringify 编码后，option 值仍是 JSON 字符串
      // 需再次解析得到对象；这里改用宽松解析，兼容并修复单引号、多余逗号等常见问题
      // 例：_rawJson='{"option":"{\\"title\\":{...}}"}'
      //     → _safeParseJson → {option: '{"title":{...}}'}
      //     → typeof === 'string' → 宽松解析 → {title: {...}}
      if (typeof opt === 'string') {
        let parsed = this._safeParseJson(opt)
        // 兼容被二次编码成字符串的情况
        if (typeof parsed === 'string') parsed = this._safeParseJson(parsed)
        opt = parsed && typeof parsed === 'object' ? parsed : null
      }

      // 兜底：将 option 中所有值为 function(...){...} 字符串的属性转为真实 Function
      if (opt && typeof opt === 'object') this._convertFunctionStrings(opt)

      // 处理嵌套结构：{ title: "...", option: { 实际echarts配置 } }
      if (opt && typeof opt === 'object' && !Array.isArray(opt) && opt.option && typeof opt.option === 'object' && !Array.isArray(opt.option)) {
        const inner = opt.option
        if (opt.title && !inner.title) {
          inner.title = opt.title
        }
        opt = inner
      }

      // 将顶层 markLine 合并到所有可用的 series 中（markLine 不属于顶层 echarts 配置）
      if (opt && opt.markLine && opt.series && Array.isArray(opt.series)) {
        const markLine = opt.markLine
        delete opt.markLine
        opt.series = opt.series.map(s => {
          if (!s.markLine) {
            return { ...s, markLine }
          }
          return s
        })
      }

      return opt
    },
    dataSource() {
      return this.parsedConfig.dataSource || null
    },
    chartData() {
      const raw = this.parsedConfig
      return this._safeParseJson(this.data) ||
        this._safeParseJson(raw.data) ||
        []
    },
    hasDataSource() {
      return !!(this.dataSource && this.dataSource.type === 'api') ||
        !!(this.parsedConfig.api && this.parsedConfig.showConfig)
    }
  },
  async mounted() {
    this.$nextTick(() => {
      if (this.hasDataSource) {
        this.fetchData().then(() => this.renderChart())
      } else {
        this.renderChart()
      }
    })
    window.addEventListener('resize', this._handleResize)
    window.addEventListener('echarts-need-resize', this._handleModeChangeResize)
  },
  beforeDestroy() {
    this._cleanupResizeObserver()
    window.removeEventListener('resize', this._handleResize)
    window.removeEventListener('echarts-need-resize', this._handleModeChangeResize)
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    _safeParseJson(value) {
      if (!value) return null
      if (typeof value === 'object') return value
      if (typeof value !== 'string') return null
      // 提取 JSON 中的 function 字符串，替换为占位符后再 JSON.parse
      const funcs = []
      let prepared = value.replace(/\n/g, '')
      // 手动扫描替换 function(...) { ... }（支持嵌套大括号）
      let searchIdx = 0
      while (searchIdx < prepared.length) {
        const fnMatch = prepared.slice(searchIdx).match(/"\s*:\s*function\s*\(/)
        if (!fnMatch) break
        const start = searchIdx + fnMatch.index
        const bodyStart = prepared.indexOf('{', start + fnMatch[0].length)
        if (bodyStart === -1) break
        // 从 bodyStart 开始数大括号层级找到匹配的 }
        let depth = 1
        let bodyEnd = -1
        for (let i = bodyStart + 1; i < prepared.length; i++) {
          if (prepared[i] === '{') depth++
          else if (prepared[i] === '}') {
            depth--
            if (depth === 0) { bodyEnd = i; break }
          }
        }
        if (bodyEnd === -1) break
        const funcBody = prepared.slice(start, bodyEnd + 1)
        const colonIdx = funcBody.indexOf(':')
        const idx = funcs.length
        funcs.push(funcBody.slice(colonIdx + 1).trim()) // "function(...) {...}"
        prepared = prepared.slice(0, start + colonIdx + 1) + ` "__ECHARTS_FUNC_${idx}__"` + prepared.slice(bodyEnd + 1)
        searchIdx = start + colonIdx + 1
      }
      try {
        const parsed = JSON.parse(prepared)
        if (funcs.length > 0) this._restoreFunctions(parsed, funcs)
        return parsed
      } catch {
        try { return JSON.parse(prepared.replace(/'/g, '"').replace(/,\s*([}\]])/g, '$1')) } catch { return null }
      }
    },
    // 递归将 option 中形如 "function(...){...}" 的字符串转为 Function 对象
    _convertFunctionStrings(obj) {
      if (!obj || typeof obj !== 'object') return
      for (const key of Object.keys(obj)) {
        const val = obj[key]
        if (typeof val === 'string' && /^\s*function\s*\(/.test(val)) {
          try { obj[key] = new Function('return ' + val)() } catch { /* ignore */ }
        } else if (typeof val === 'object') {
          this._convertFunctionStrings(val)
        }
      }
    },
    _restoreFunctions(obj, funcs) {
      if (!obj || typeof obj !== 'object') return
      for (const key of Object.keys(obj)) {
        const val = obj[key]
        if (typeof val === 'string' && val.startsWith('__ECHARTS_FUNC_')) {
          const idx = parseInt(val.replace('__ECHARTS_FUNC_', '').replace('__', ''), 10)
          if (!isNaN(idx) && funcs[idx]) {
            try {
              obj[key] = new Function('return ' + funcs[idx])()
            } catch { /* ignore invalid function */ }
          }
        } else if (typeof val === 'object') {
          this._restoreFunctions(val, funcs)
        }
      }
    },
    _get(data, path) {
      if (!data || !path) return data
      const keys = path.split('.')
      let result = data
      for (const key of keys) {
        if (result == null) return null
        result = result[key]
      }
      return result
    },
    async fetchData() {
      // 新格式：api + showConfig 动态渲染
      if (this.parsedConfig.api && this.parsedConfig.showConfig) {
        await this._buildFromApiConfig()
        return
      }

      // 旧格式：dataSource 数据源
      const ds = this.dataSource
      if (!ds || !ds.api) return

      this.loading = true
      this.loadError = ''
      this.loadingText = ds.loadingText || (window.$i18n ? window.$i18n.t('workbench.loading') : 'Loading...')

      try {
        const { url, method = 'GET', params = {}, headers = {}} = ds.api
        let response
        if (method.toUpperCase() === 'GET') {
          response = await axios.get(url, { params, headers })
        } else {
          response = await axios({ url, method, data: params, headers })
        }

        // 从响应中提取数据
        const rawData = response.data
        const recordsPath = ds.responsePath || 'data.records'
        let dataArray = this._get(rawData, recordsPath)

        if (!Array.isArray(dataArray)) {
          dataArray = Array.isArray(rawData) ? rawData : null
        }

        if (!dataArray || dataArray.length === 0) {
          this.loadError = ds.emptyText || (window.$i18n ? window.$i18n.t('workbench.noFormConfig') : 'No data')
          this.loading = false
          return
        }

        // 将数据映射到 option
        this.resolvedOption = this._buildOptionFromData(dataArray, this.parsedOption || {})
      } catch (e) {
        this.loadError = e.message || (window.$i18n ? window.$i18n.t('workbench.requestFailedSimple') : 'Request failed')
      } finally {
        this.loading = false
      }
    },

    async _buildFromApiConfig() {
      const config = this.parsedConfig
      const { api, showConfig, baseOption, dataMapping } = config

      this.loading = true
      this.loadError = ''
      this.loadingText = config.loadingText || (window.$i18n ? window.$i18n.t('workbench.loading') : 'Loading...')

      try {
        const { url, method = 'POST', params = {}, headers: rawHeaders = {}} = api
        const headers = { ...rawHeaders }
        let response
        if (method.toUpperCase() === 'GET') {
          response = await axios.get(url, { params, headers })
        } else {
          response = await axios({ url, method, data: params, headers })
        }

        const rawData = response.data
        const seriesFields = dataMapping?.seriesData || []
        const markLineMapping = dataMapping?.markLine || {}

        // 辅助：递归取字段，支持嵌套路径（如 result.pointListBar）
        const getField = (path) => {
          if (!path) return undefined
          // 先尝试完整路径
          let val = this._get(rawData, path)
          if (val !== undefined && val !== null) { return val }
          // 再尝试 result. 前缀（通用 API 包装层）
          val = this._get(rawData, 'result.' + path)
          if (val !== undefined && val !== null) { return val }
          // 再尝试 data. 前缀
          val = this._get(rawData, 'data.' + path)
          return val
        }

        // 深拷贝 baseOption
        const option = JSON.parse(JSON.stringify(baseOption || {}))

        // 从 showConfig + seriesData 构建 series
        option.series = showConfig.map((s, i) => {
          const field = seriesFields[i]
          let raw = field ? getField(field) : []

          // 兼容 API 返回的字符串化 JSON 数组
          if (typeof raw === 'string') {
            try { raw = JSON.parse(raw) } catch { raw = [] }
          }

          if (!Array.isArray(raw)) raw = []

          // 判断数据格式：[[x,y],...] 坐标对 或 简单数值数组
          const isXY = raw.length > 0 && Array.isArray(raw[0]) && raw[0].length >= 2

          let data
          if (isXY) {
            // 坐标对格式直接透传（xAxis.type: 'value' 时 ECharts 原生支持）
            data = raw
          } else {
            // 简单数值或对象提取
            data = raw.map(d => {
              if (d == null) return null
              if (typeof d === 'number') return d
              if (typeof d === 'object') {
                if (d.value !== undefined) return Number(d.value)
                const numVal = Object.values(d).find(v => typeof v === 'number')
                return numVal != null ? Number(numVal) : null
              }
              return Number(d) || null
            })
          }

          const series = { ...s, type: s.type || 'line', name: s.name || '', data }
          // 折线图默认平滑
          if (s.type === 'line') series.smooth = true
          // 删除 showConfig 中不需要透传给 ECharts 的属性
          delete series.field

          return series
        })

        // 处理 markLine（从响应中取值，作为 xAxis 垂直线）
        const markLineData = []
        const lslVal = getField(markLineMapping.lsl)
        const uslVal = getField(markLineMapping.usl)
        const nomVal = getField(markLineMapping.nom)

        // 每条线在顶部展示标签，文字旋转 90° 与线方向平行（垂直阅读），紧贴在线条上
        const markItems = [
          { value: lslVal, label: 'LSL', color: '#ee6666', hasMapping: markLineMapping.lsl },
          { value: nomVal, label: 'NOM', color: '#91cc75', hasMapping: markLineMapping.nom },
          { value: uslVal, label: 'USL', color: '#ee6666', hasMapping: markLineMapping.usl }
        ]
        markItems.forEach(item => {
          if (item.hasMapping && item.value !== undefined && item.value !== null) {
            markLineData.push({
              xAxis: item.value,
              label: {
                formatter: () => `${item.label}:${item.value}`,
                position: 'middle',
                rotate: -90,
                fontSize: 8
              },
              lineStyle: { color: item.color, width: 2, type: 'dashed' }
            })
          }
        })

        if (markLineData.length > 0) {
          option.series = option.series.map(s => ({
            ...s,
            markLine: { silent: true, symbol: 'none', data: markLineData }
          }))
        }

        // 根据 LSL/USL 和实际数据范围自动调整 xAxis min/max
        if ((lslVal !== undefined && lslVal !== null) || (uslVal !== undefined && uslVal !== null)) {
          const xAxis = option.xAxis || {}
          const pad = 2
          const xMin = xAxis.min
          const xMax = xAxis.max
          const hasLsl = lslVal !== undefined && lslVal !== null
          const hasUsl = uslVal !== undefined && uslVal !== null

          // 遍历所有 series 数据，找出实际 X 轴最小值和最大值
          let dataMin = Infinity
          let dataMax = -Infinity
          if (option.series && Array.isArray(option.series)) {
            option.series.forEach(s => {
              if (s.data && Array.isArray(s.data)) {
                s.data.forEach(d => {
                  const x = Array.isArray(d) ? d[0] : (typeof d === 'number' ? d : null)
                  if (x !== null && x !== undefined && isFinite(x)) {
                    if (x < dataMin) dataMin = x
                    if (x > dataMax) dataMax = x
                  }
                })
              }
            })
          }

          if (xMin === undefined || xMin === null) {
            const lslBound = hasLsl ? Math.floor(lslVal) - pad : Infinity
            // 如果实际最小值比 lsl-pad 还小，用实际值；否则用 lsl-pad
            option.xAxis.min = isFinite(dataMin) ? Math.min(dataMin, lslBound) : (hasLsl ? lslBound : 0)
          }
          if (xMax === undefined || xMax === null) {
            const uslBound = hasUsl ? Math.ceil(uslVal) + pad : -Infinity
            // 如果实际最大值比 usl+pad 还大，用实际值；否则用 usl+pad
            option.xAxis.max = isFinite(dataMax) ? Math.max(dataMax, uslBound) : (hasUsl ? uslBound : 100)
          }
        }

        this.resolvedOption = option
      } catch (e) {
        this.loadError = e.message || (window.$i18n ? window.$i18n.t('workbench.requestFailedSimple') : 'Request failed')
      } finally {
        this.loading = false
      }
    },
    _buildOptionFromData(dataArray, optionTemplate) {
      // 深拷贝模板，避免修改原始对象
      const option = JSON.parse(JSON.stringify(optionTemplate))
      const mapping = option.mapping || {}
      const xField = mapping.xField || 'x'
      const labelFormatter = mapping.labelFormatter

      // 提取 x 轴数据
      const xData = dataArray.map(d => {
        const val = this._get(d, xField)
        return val != null ? String(val) : ''
      })
      if (!option.xAxis) option.xAxis = { type: 'category' }
      option.xAxis.data = xData

      // 提取系列数据
      if (option.series && Array.isArray(option.series)) {
        option.series = option.series.map(s => {
          const field = s.field
          if (field && dataArray.length > 0) {
            s = { ...s }
            if (!s.data || (Array.isArray(s.data) && s.data.length === 0)) {
              s.data = dataArray.map(d => {
                const v = this._get(d, field)
                return v != null ? Number(v) : null
              })
            }
          }
          // 饼图数据特殊处理：xField + field 作为 name/value
          if (s.type === 'pie' && field) {
            s.data = dataArray.map(d => ({
              name: String(this._get(d, xField) ?? ''),
              value: Number(this._get(d, field) ?? 0)
            }))
            delete option.xAxis
          }
          return s
        })
      } else {
        // 没有显式 series 定义时，自动将非 xField 字段生成为 bar 系列
        const allKeys = dataArray.length > 0 ? Object.keys(dataArray[0]) : []
        const fieldKeys = allKeys.filter(k => k !== xField)
        if (fieldKeys.length > 0) {
          option.series = fieldKeys.map((k, idx) => ({
            name: k,
            type: idx === 0 ? 'bar' : 'line',
            data: dataArray.map(d => {
              const v = this._get(d, k)
              return v != null ? Number(v) : null
            })
          }))
        }
      }

      if (labelFormatter) {
        option.yAxis = option.yAxis || {}
        option.yAxis.axisLabel = option.yAxis.axisLabel || {}
        option.yAxis.axisLabel.formatter = labelFormatter
      }

      return option
    },
    // 计算动态容器高度
    // - 水平柱状图（类目在 yAxis）：按条数增高，保底 300px
    // - 垂直柱状图/折线图（类目在 xAxis，带旋转标签）：保底 320px，多 20px 给 grid.bottom 补偿旋转空间
    // - 饼图等无类目轴：保底 300px
    _calculateDynamicHeight(option) {
      if (!option || typeof option !== 'object') return 300
      const perItemHeight = 35
      const reservedHeight = 120

      // 检测水平柱状图：yAxis 有 category data
      let yCategoryCount = 0
      if (option.yAxis) {
        const yAxes = Array.isArray(option.yAxis) ? option.yAxis : [option.yAxis]
        yAxes.forEach(axis => {
          if (axis && Array.isArray(axis.data) && (!axis.type || axis.type === 'category')) {
            yCategoryCount = Math.max(yCategoryCount, axis.data.length)
          }
        })
      }
      if (yCategoryCount > 0) {
        return Math.max(300, yCategoryCount * perItemHeight + reservedHeight)
      }

      // 检测垂直柱状图/折线图：xAxis 有 category data
      // 按最长类目名称长度决定高度：超过 10 字 → 500px，否则 320px
      let maxLabelLen = 0
      if (option.xAxis) {
        const xAxes = Array.isArray(option.xAxis) ? option.xAxis : [option.xAxis]
        xAxes.forEach(axis => {
          if (axis && Array.isArray(axis.data) && (!axis.type || axis.type === 'category')) {
            axis.data.forEach(label => {
              const len = String(label).length
              if (len > maxLabelLen) maxLabelLen = len
            })
          }
        })
      }
      if (maxLabelLen === 0) return 300
      return maxLabelLen > 10 ? 500 : 320
    },
    _applyDynamicHeight() {
      const container = this.$refs.chartContainer
      if (!container) return
      const opt = this.sourceOption || this.resolvedOption || this.parsedOption
      container.style.height = this._calculateDynamicHeight(opt) + 'px'
    },
    // 兜底卡片修复成功：用修复后的 JSON 替换原始内容并重新渲染
    handleFallbackFixed(result) {
      if (!result || !result.valid || result.fallback || !result.cardJson) return
      const payload = result.payload || reparseComponentJson(result.cardJson)
      if (!payload || !payload.props) return
      const rawJson = payload.props._rawJson
        ? String(payload.props._rawJson)
        : JSON.stringify(payload.props)
      this.fixedRawJson = rawJson
      this.resolvedOption = null
      this.sourceOption = null
      if (this.chart && !this.chart._disposed) this.chart.dispose()
      this.chart = null
      this.renderFailed = false
      this.$nextTick(() => this.renderChart())
    },
    renderChart() {
      // 没有 chartContainer（在 v-if 隐藏状态）或已有错误时跳过
      const container = this.$refs.chartContainer
      if (!container) return
      if (this.loadError) return
      this.renderFailed = false

      this.chart = echarts.init(container)
      this._setupResizeObserver()

      const parsedOpt = this.parsedOption

      // 显式resize并传入尺寸的辅助函数
      const explicitResize = () => {
        this.$nextTick(() => {
          if (this.chart && !this.chart._disposed) {
            const w = container.offsetWidth || 600
            const h = container.offsetHeight || 300
            this.chart.resize({ width: w, height: h })
          }
        })
      }

      // 在 sourceOption 赋值后应用动态高度，确保拿到完整 option
      const applyDynamicHeightAndResize = () => {
        this._applyDynamicHeight()
        // 重新获取真实容器尺寸
        const w = container.offsetWidth || 600
        const h = container.offsetHeight || 300
        this.lastContainerWidth = w
        this.lastContainerHeight = h
        this.chart.resize({ width: w, height: h })
      }

      // 1. 有数据源解析结果（api + showConfig + baseOption 动态构建）
      if (this.resolvedOption) {
        this.sourceOption = this.resolvedOption
        applyDynamicHeightAndResize()
        const containerWidth = container.offsetWidth || 600
        const normalized = mergeWithDefault(this.resolvedOption, containerWidth)
        this.chart.setOption(normalized)
        explicitResize()
        return
      }

      // 2. 有直接传入的 ECharts option
      if (parsedOpt) {
        try {
          this.sourceOption = parsedOpt
          applyDynamicHeightAndResize()
          const containerWidth = container.offsetWidth || 600
          const normalized = mergeWithDefault(parsedOpt, containerWidth)
          this.chart.setOption(normalized)
          explicitResize()
        } catch (e) {
          console.error('[MdChart] ECharts setOption error:', e)
          this.renderFailed = true
        }
        return
      }

      // 没有有效 option 配置时显示错误
      if (this.option || (this._rawJson && this.parsedConfig.option)) {
        this.renderFailed = true
        return
      }

      // 3. 向后兼容：通过 type / data / xfield / yfield 构建简单图表
      if (!this.chartData || this.chartData.length === 0) return

      const xKey = this.xfield || 'x'
      const yKey = this.yfield || 'y'
      const xData = this.chartData.map(d => d[xKey] || d[Object.keys(d)[0]])
      const yData = this.chartData.map(d => {
        const v = d[yKey] || d[Object.keys(d)[1]]
        return v !== null && v !== undefined ? Number(v) : v
      })

      let option = {}

      if (this.type === 'line') {
        option = {
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: xData },
          yAxis: { type: 'value' },
          series: [{ type: 'line', data: yData, smooth: true, areaStyle: { opacity: 0.1 }}]
        }
      } else if (this.type === 'bar') {
        option = {
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: xData },
          yAxis: { type: 'value' },
          series: [{ type: 'bar', data: yData, itemStyle: { color: '#3370ff' }}]
        }
      } else if (this.type === 'pie') {
        const pieData = this.chartData.map(d => ({
          name: d[xKey] || d[Object.keys(d)[0]],
          value: Number(d[yKey] || d[Object.keys(d)[1]] || 0)
        }))
        option = {
          tooltip: { trigger: 'item' },
          series: [{
            type: 'pie',
            radius: ['40%', '70%'],
            data: pieData,
            label: { show: true, formatter: '{b}: {d}%' }
          }]
        }
      }

      if (Object.keys(option).length > 0) {
        this.sourceOption = option
        applyDynamicHeightAndResize()
        const containerWidth = container.offsetWidth || 600
        const normalized = mergeWithDefault(option, containerWidth)
        this.chart.setOption(normalized)
        explicitResize()
      }
    },
    _setupResizeObserver() {
      const container = this.$refs.chartContainer
      if (!container) return
      this._cleanupResizeObserver()
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver((entries) => {
          const entry = entries && entries[0]
          if (entry && entry.contentRect) {
            const newWidth = Math.round(entry.contentRect.width)
            const newHeight = Math.round(entry.contentRect.height)
            // 只有尺寸真正变化时才触发resize
            if (newWidth !== this.lastContainerWidth || newHeight !== this.lastContainerHeight) {
              this.lastContainerWidth = newWidth
              this.lastContainerHeight = newHeight
              this._handleResize()
            }
          } else {
            // 兜底：没有contentRect时直接触发
            this._handleResize()
          }
        })
        this.resizeObserver.observe(container)
      }
    },
    _handleResize() {
      if (!this.chart || this.chart._disposed) return
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.chart && !this.chart._disposed) {
          const container = this.$refs.chartContainer
          if (!container) return
          // resize 前重新应用动态高度，防止容器高度被重置
          this._applyDynamicHeight()
          const containerWidth = container.offsetWidth || 600
          const containerHeight = container.offsetHeight || 300

          // 使用保存的原始option重新应用响应式配置（避免getOption返回数组形式破坏title等配置）
          if (this.sourceOption) {
            try {
              const normalized = mergeWithDefault(this.sourceOption, containerWidth)
              this.chart.setOption(normalized, true)
            } catch (e) {
              // 忽略setOption错误
            }
          }
          // 显式resize并传入尺寸
          this.chart.resize({
            width: containerWidth,
            height: containerHeight
          })
        }
      }, 200)
    },
    _handleModeChangeResize() {
      if (!this.chart || this.chart._disposed) return
      // 模式切换后延迟更长时间，等待CSS Grid布局动画完成
      clearTimeout(this.modeChangeTimer)
      this.modeChangeTimer = setTimeout(() => {
        if (this.chart && !this.chart._disposed) {
          const container = this.$refs.chartContainer
          if (!container) return
          // resize 前重新应用动态高度，防止容器高度被重置
          this._applyDynamicHeight()
          const containerWidth = container.offsetWidth || 600
          const containerHeight = container.offsetHeight || 300
          // 更新记录的尺寸
          this.lastContainerWidth = containerWidth
          this.lastContainerHeight = containerHeight

          // 使用保存的原始option重新应用响应式配置
          if (this.sourceOption) {
            try {
              const normalized = mergeWithDefault(this.sourceOption, containerWidth)
              this.chart.setOption(normalized, true)
            } catch (e) {
              // 忽略setOption错误
            }
          }
          // 显式resize并传入尺寸参数
          this.chart.resize({
            width: containerWidth,
            height: containerHeight
          })
        }
      }, 500)
    },
    _cleanupResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
        this.resizeTimer = null
      }
      if (this.modeChangeTimer) {
        clearTimeout(this.modeChangeTimer)
        this.modeChangeTimer = null
      }
    }
  },
  watch: {
    data() {
      if (this.chart) this.chart.dispose()
      this.$nextTick(() => this.renderChart())
    },
    option() {
      if (this.chart) this.chart.dispose()
      this.$nextTick(() => this.renderChart())
    }
  }
}
</script>

<style lang="scss" scoped>
.md-chart {
  margin: 8px 0;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 0;
  background: #fff;
  width: 100%;
  overflow: hidden;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
  padding: 32px 16px 4px 16px;
  margin: 0;
}

.chart-container {
  width: 100%;
  height: 300px;
  display: block;
  position: relative;

  canvas {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }
}
</style>
